import type { ConditionalKeys } from 'type-fest';
import { trackError } from '#api/errors.ts';
import { useSuspenseFeatureFlagsQuery } from '#api/org.queries.ts';
import type { FeatureFlagsResult } from '#api/org.ts';
import { NotFound } from '#screens/404/NotFound.tsx';
import { Protected } from './Protected.tsx';

type Props = {
  /** {@link featureFlagIdent} only allows feature flags with a boolean, number, or undefined value */
  featureFlagIdent: ConditionalKeys<FeatureFlagsResult, boolean | number | undefined>;
  children: React.ReactNode;
  redirectTo?: string;
};

/**
 * {@link FeatureFlagged} is a component that does not allow access without the feature flag enabled.
 */
export function FeatureFlagged({ featureFlagIdent, children, redirectTo }: Props) {
  // Get feature flags
  const { data: featureFlags } = useSuspenseFeatureFlagsQuery();

  const ff = featureFlags[featureFlagIdent];

  // We don't want to render the child if the feature flag is not a boolean or number
  if (typeof ff !== 'boolean' && typeof ff !== 'number') {
    trackError(
      `FeatureFlagged only works with boolean or number flags, ${featureFlagIdent} typeof value is ${typeof ff}`
    );
    return <NotFound />;
  }

  return (
    <Protected isPermitted={() => Boolean(ff)} redirectTo={redirectTo}>
      {children}
    </Protected>
  );
}
